import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Box, Button, Checkbox, Flex, FormControl, FormLabel, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import InputMy from 'components/InputMy';
import { HSeparator } from 'components/separator/Separator';
import AuthButton from 'components/singIn/AuthButton';
import Subtitle from 'components/singIn/Subtitle';
import TextWithLink from 'components/singIn/TextWithLink';
import Title from 'components/singIn/Title';
import { useFormik } from 'formik';
import DefaultAuth from 'layouts/auth/Default';
import { FcGoogle } from 'react-icons/fc';
import { loginValidationSchema } from 'validations';
import AuthService from 'api/services/AuthService';
import { setAuthToken } from 'api/helpers';
import { MAIN_URL } from 'routes';

function SignIn() {
    // Chakra color mode
    const navigate = useNavigate();
    const googleBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.200');
    const googleHover = useColorModeValue({ bg: 'gray.200' }, { bg: 'whiteAlpha.300' });
    const googleActive = useColorModeValue({ bg: 'secondaryGray.300' }, { bg: 'whiteAlpha.200' });

    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema: loginValidationSchema,
        onSubmit: async params => {
            console.log(params);
            try {
                setLoading(true);
                await AuthService.login(params)
                    .then(response => {
                        console.log('response', response);
                        setLoading(false);
                        setAuthToken(response.data.token);
                        navigate(MAIN_URL);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } catch (e) {
                return false;
            }
        },
    });

    return (
        <DefaultAuth>
            <div>
                <Box>
                    <Title>Sign In</Title>
                    <Subtitle>Enter your email and password to sign in!</Subtitle>
                </Box>
                <Flex
                    zIndex="2"
                    direction="column"
                    w={{ base: '100%', md: '420px' }}
                    maxW="100%"
                    background="transparent"
                    borderRadius="15px"
                    mx={{ base: 'auto', lg: 'unset' }}
                    me="auto"
                    mb={{ base: '20px', md: 'auto' }}
                >
                    <Button
                        fontSize="sm"
                        mb="26px"
                        py="14px"
                        h="50px"
                        borderRadius="12px"
                        bg={googleBg}
                        color="gray.900"
                        fontWeight="600"
                        _hover={googleHover}
                        _active={googleActive}
                        _focus={googleActive}
                    >
                        <Icon as={FcGoogle} w="20px" h="20px" me="10px" />
                        Sign in with Google
                    </Button>
                    <Flex align="center" mb="25px">
                        <HSeparator />
                        <Text color="gray.400" mx="14px">
                            or
                        </Text>
                        <HSeparator />
                    </Flex>
                    <FormControl>
                        <InputMy
                            name="email"
                            label="Email"
                            required
                            disabled={loading}
                            type="email"
                            placeholder="example@mail.com"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                            onBlur={formik.handleBlur}
                            sx={{ marginBottom: '24px' }}
                        />
                        <InputMy
                            name="password"
                            label="Password"
                            required
                            disabled={loading}
                            type="password"
                            placeholder="password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                            onBlur={formik.handleBlur}
                            sx={{ marginBottom: '24px' }}
                        />
                        <Flex justifyContent="space-between" align="center" mb="24px">
                            <FormControl display="flex" alignItems="center">
                                <Checkbox id="remember-login" colorScheme="brandScheme" me="10px" size="lg" />
                                <FormLabel
                                    htmlFor="remember-login"
                                    mb="0"
                                    fontWeight="500"
                                    color="gray.900"
                                    fontSize="md"
                                >
                                    Keep me logged in
                                </FormLabel>
                            </FormControl>
                            <NavLink to="/auth/password-recovery">
                                <Text color="brand.500" fontSize="md" w="130px" fontWeight="500">
                                    Forgot password?
                                </Text>
                            </NavLink>
                        </Flex>
                        <AuthButton onClick={formik.submitForm} loading={loading}>
                            Sign In
                        </AuthButton>
                    </FormControl>
                    <TextWithLink text="Not registered yet?" linkText="Create an Account" href="/auth/sign-up" />
                </Flex>
            </div>
        </DefaultAuth>
    );
}

export default SignIn;
